import React, { useEffect, useState } from 'react'
import classnames from 'classnames'
import { intl } from 'lang/intl'
import { usePrevious } from 'react-use'
import { FormattedMessage } from 'react-intl'

import { TtButton as Button } from '@23/ui-react'

import { WebinarInfoSidebarComponent } from '../../../../components/WebinarInfoSidebarComponent/WebinarInfoSidebarComponent'
import { HandoutsList } from '../../../../../Handouts/components/HandoutsList'
import { useWebinarColors } from '../../../../../Designs/blocs/useWebinarColors'
import SessionsDrawer from '../../../WebinarSidebarFeature/components/WebinarSidebarComponent/SessionsDrawer/SessionsDrawer'
import { WebinarInfoAudienceSidebarComponent } from '../WebinarInfoAudienceSidebarComponent/WebinarInfoAudienceSidebarComponent'
import { WebinarSidebarSecondaryPanelComponent } from '../../../WebinarSidebarFeature/components'
import { Polls } from '../../../../../Polls/components/Polls'
import { usePollsList } from '../../../../../Polls/blocs/usePolls'
import useFeatures from '../../../../hooks/useFeatures'
import { Chat } from '../../../../../Chat/components/Chat/Chat'
import { useActiveActionCardsQuery } from '../../../../../ActionCards/blocs/useActionCards'
import { ActionCard } from '../../../../../ActionCards/components/ActionCard'
import { useLiveDataQuery } from '../../../../../global/blocs/useLiveData'

import { renderPollUnitWrapper } from './renderPollUnitWrapper'

import './WebinarAudienceSidebarComponent.scss'

interface WebinarAudienceMobileSidebarProps {
  toggleSecondaryPanel: (panel: null | 'sessions') => void
  isInGearMode: boolean
  secondaryPanelContentType: null | 'sessions'
}

type MobileDeviceTab = 'chat' | 'other' | 'sessions'

const WebinarAudienceMobileSidebarComponent = ({
  toggleSecondaryPanel,
  isInGearMode,
  secondaryPanelContentType,
}: WebinarAudienceMobileSidebarProps) => {
  const webinarColors = useWebinarColors()

  const { data: liveData } = useLiveDataQuery()

  const { HasWebinarActionCardsEnabled } = useFeatures()

  const audienceChatEnabled =
    liveData?.comment_mode === 'all' || liveData?.comment_mode === 'speakers'

  const [mobileTabSelected, setMobileTabSelected] = useState<MobileDeviceTab>(
    audienceChatEnabled ? 'chat' : 'other'
  )

  const { activePoll } = usePollsList({
    publicOnly: true,
    isVotingAllowed: true,
  })

  const { data: activeActionCard } = useActiveActionCardsQuery()
  const prevActiveActionCard = usePrevious(activeActionCard)

  const prevActivePoll = usePrevious(activePoll)

  useEffect(() => {
    if (
      audienceChatEnabled &&
      mobileTabSelected === 'chat' &&
      activePoll &&
      activePoll.pollId !== prevActivePoll?.pollId
    ) {
      setMobileTabSelected('other')
    }
  }, [audienceChatEnabled, mobileTabSelected, prevActivePoll, activePoll])

  useEffect(() => {
    if (
      audienceChatEnabled &&
      mobileTabSelected === 'chat' &&
      activeActionCard &&
      activeActionCard.id !== prevActiveActionCard?.id
    ) {
      setMobileTabSelected('other')
    }
  }, [
    activeActionCard,
    audienceChatEnabled,
    mobileTabSelected,
    prevActiveActionCard?.id,
  ])

  const cssVars = {
    '--webinar-color-accent': webinarColors.accent.color,
    '--webinar-color-sidebar': webinarColors.sidebar.color,
    '--webinar-color-tabs': webinarColors.tabs.color,
    '--webinar-color-sidebar-text': webinarColors.sidebar.highContrast.color,
    '--webinar-color-tabs-text': webinarColors.tabs.highContrast.color,
    '--webinar-color-accent-text': webinarColors.accent.highContrast.color,
    '--webinar-color-sidebarSecondaryText':
      webinarColors.tabs.highContrast.transparent('85%'),
    '--webinar-color-inputBox':
      webinarColors.sidebar.highContrast.color === '#000000'
        ? 'rgba(0, 0, 0, 0.05)'
        : 'rgba(0, 0, 0, 0.15)',
  } as React.CSSProperties

  const isDigitalEvent = !!liveData?.live_series_id

  if (!liveData) return null

  return (
    <div
      className="webinar-audience-sidebar-component is-mobile-device"
      style={cssVars}
    >
      <div
        className={classnames('sidebar-main-panel-wrapper', 'is-mobile-device')}
        style={{
          backgroundColor: webinarColors.sidebar.color,
          color: webinarColors.sidebar.highContrast.color,
        }}
      >
        <div className="webinar-mobile-tab-selector-component">
          <Button
            className={classnames('webinar-mobile-tab-selector-button', {
              'webinar-mobile-tab-selector-button-selected':
                mobileTabSelected === 'chat',
            })}
            size="small"
            label={
              audienceChatEnabled
                ? intl.formatMessage({ defaultMessage: 'Chat' })
                : intl.formatMessage({ defaultMessage: 'Info' })
            }
            onClick={() => {
              setMobileTabSelected('chat')
            }}
          >
            {audienceChatEnabled ? (
              <FormattedMessage defaultMessage="Chat" />
            ) : (
              <FormattedMessage defaultMessage="Info" />
            )}
          </Button>
          <Button
            className={classnames('webinar-mobile-tab-selector-button', {
              'webinar-mobile-tab-selector-button-selected':
                mobileTabSelected === 'other',
            })}
            size="small"
            label={intl.formatMessage({ defaultMessage: 'Feedback' })}
            onClick={() => {
              setMobileTabSelected('other')
            }}
          >
            <FormattedMessage defaultMessage="Feedback" />
          </Button>
          {isDigitalEvent && (
            <Button
              className={classnames('webinar-mobile-tab-selector-button', {
                'webinar-mobile-tab-selector-button-selected':
                  mobileTabSelected === 'sessions',
              })}
              size="small"
              label={intl.formatMessage({ defaultMessage: 'Webinars' })}
              onClick={() => {
                toggleSecondaryPanel('sessions')
                setMobileTabSelected('sessions')
              }}
            >
              <FormattedMessage defaultMessage="Webinars" />
            </Button>
          )}
        </div>

        {mobileTabSelected === 'chat' && (
          <WebinarInfoAudienceSidebarComponent isMobileDevice />
        )}

        <div
          className={classnames('webinar-mobile-other-tab-wrapper', {
            'display-none':
              mobileTabSelected === 'chat' || mobileTabSelected === 'sessions',
          })}
        >
          {HasWebinarActionCardsEnabled && <ActionCard />}
          <Polls
            renderPollUnitWrapper={renderPollUnitWrapper}
            disableOverlayTrigger={isInGearMode}
            isPublicPollOnly
            canChangePoll={false}
            isVotingAllowed
          />
          <HandoutsList liveToken={liveData.token} />
        </div>

        {mobileTabSelected === 'chat' && !audienceChatEnabled && (
          <WebinarInfoSidebarComponent />
        )}

        {audienceChatEnabled && mobileTabSelected === 'chat' && <Chat />}

        {mobileTabSelected === 'sessions' && secondaryPanelContentType && (
          <div className="secondary-panel-wrapper">
            <WebinarSidebarSecondaryPanelComponent>
              {
                {
                  sessions: (
                    <SessionsDrawer
                      seriesId={Number(liveData.live_series_id)}
                      seriesToken={liveData.live_series_token}
                      webinarId={Number(liveData.live_id)}
                    />
                  ),
                }[secondaryPanelContentType]
              }
            </WebinarSidebarSecondaryPanelComponent>
          </div>
        )}
      </div>
    </div>
  )
}

export { WebinarAudienceMobileSidebarComponent }
